import React from 'react';
import { Button, FormContainer } from '@utex/ui-kit';
import { useTranslation } from 'react-i18next';

import { Container, Form, FormContent, FormFooter, Factoids } from './styles';
import { Title } from './components/Title';
import { AmountInput } from '../AmountInput';
import { Balance } from '../Balance';
import { useConversionService } from '../../hooks/useConversionService';
import { createValidator } from '@unitedtraders/validators';
import { useAmountValidators } from '../AmountInput/hooks';
import { ExchangeRate } from '../ExchangeRate';
import { Money } from '../MoneyView';
import { Result } from '../Result';
import { DirectionSwitcher } from '../DirectionSwitcher';
import { observer } from 'mobx-react';
import { useBalance } from '../../hooks/useBalance';
import { FormError } from '../FormError';

interface Props {}

export interface FormValues {
  amount: string;
}

export const FormView: React.FC<Props> = observer(() => {
  const { t } = useTranslation('conversion');
  const { currencyFrom, exchange } = useConversionService();
  const { balance } = useBalance(currencyFrom);

  const validate = createValidator<FormValues>({
    amount: useAmountValidators(currencyFrom),
  });

  return (
    <Container>
      <Title />
      <FormContainer<FormValues>
        onSubmit={({ amount }) => exchange(+amount)}
        validate={validate}
        render={({ handleSubmit, values, submitting, submitError, form }) => {
          const amount: Money = {
            value: +values.amount,
            currency: currencyFrom,
          };
          return (
            <Form onSubmit={handleSubmit} noValidate>
              <FormContent>
                <DirectionSwitcher onClick={() => form.reset({ amount: '' })} />
                <AmountInput
                  name="amount"
                  currency={currencyFrom}
                  caption={<Result amount={amount} />}
                  max={balance?.value}
                />
                <Factoids>
                  <Balance
                    currency={currencyFrom}
                    onClick={() => balance && form.reset({ amount: format(balance.value) })}
                  />
                  <ExchangeRate pair={currencyFrom === 'USD' ? 'USDRUB' : 'RUBUSD'} />
                </Factoids>
                <FormError>{submitError}</FormError>
              </FormContent>
              <FormFooter>
                <Button view="alert" type="submit" isLoading={submitting}>
                  {t('Exchange')}
                </Button>
              </FormFooter>
            </Form>
          );
        }}
      />
    </Container>
  );
});

const format = (value: number) => {
  const precision = 2;
  const result = `${value}`;
  const [, decimals] = result.split('.');

  if (decimals && decimals.length > precision) {
    return result.substr(0, result.length - (decimals.length - precision));
  }

  return result;
};
